import matter from "gray-matter";

const CONTENT_PATH_RE = /content\/(de|en)\/([^/]+)\/([^/]+)\.md$/;

function parseModules(modules) {
  return Object.entries(modules).map(([path, raw]) => {
    const [, lang, , fileSlug] = path.match(CONTENT_PATH_RE);
    const { data, content } = matter(raw);
    return { lang, fileSlug, frontmatter: data, content };
  });
}

const eventFiles = import.meta.glob("../../content/*/events/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});
const larpFiles = import.meta.glob("../../content/*/larp-erleben/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});
const spendenFiles = import.meta.glob("../../content/*/spenden/*.md", {
  eager: true,
  query: "?raw",
  import: "default",
});

export const events = parseModules(eventFiles);
export const larpPages = parseModules(larpFiles);
export const spendenProjects = parseModules(spendenFiles);

export function getEventsForLang(lang) {
  return events.filter((e) => e.lang === lang);
}

export function getEventBySlug(lang, slug) {
  return events.find((e) => e.lang === lang && e.frontmatter.slug === slug);
}

/**
 * The most recent event that has already happened, in `lang` — resolved
 * from the content set rather than hardcoded, so the homepage's "our last
 * tavern night" link can never again point at the OTHER language's slug
 * (an /en/ link to a /de/-only slug was a live 404 — § T-03). Prefers a
 * real tavern night; falls back to any past event so the link still has
 * somewhere to go before the next tavern night exists.
 */
export function getLatestPastEvent(lang) {
  const past = getEventsForLang(lang)
    .filter((e) => e.frontmatter.status === "past")
    .sort((a, b) => new Date(b.frontmatter.date) - new Date(a.frontmatter.date));
  return past.find((e) => e.frontmatter.category === "taverne") ?? past[0];
}

/**
 * "Für Orgas" case studies: events organized by an external/partner orga
 * that Tales&Legends acted as legal Träger for, opted in via
 * `externalOrga: true` frontmatter (own events omit the field entirely).
 * Most recent first, same sort/limit shape as getFeaturedLarpPages.
 */
export function getExternalOrgaEvents(lang, limit = 3) {
  return getEventsForLang(lang)
    .filter((e) => e.frontmatter.externalOrga === true)
    .sort((a, b) => new Date(b.frontmatter.date) - new Date(a.frontmatter.date))
    .slice(0, limit);
}

/** `slug` is an explicit frontmatter field (like events/spenden), chosen
 * independently of the filename — not derived from `fileSlug`. */
export function getLarpPage(lang, slug) {
  return larpPages.find((p) => p.lang === lang && p.frontmatter.slug === slug);
}

/**
 * Cross-language matching for events/larp-erleben/spenden, self-derived
 * from each item's own `id` frontmatter field instead of a hand-maintained
 * slug-pair table — a DE and an EN item are the same piece of content iff
 * they share an `id`, regardless of what their (language-appropriate)
 * slugs happen to be. `items` is one of `events`/`larpPages`/
 * `spendenProjects`. Returns undefined if the item has no `id` or no
 * sibling exists in `lang` — callers treat that as "no translation".
 */
export function findByLangAndId(items, lang, id) {
  return id == null ? undefined : items.find((item) => item.lang === lang && item.frontmatter.id === id);
}

/**
 * Homepage "Wissen" teaser: editors opt articles in via `featured: true`
 * frontmatter. If more than `limit` are marked featured, only the most
 * recently created ones (by `date` frontmatter) survive the cut.
 */
export function getFeaturedLarpPages(lang, limit = 3) {
  return larpPages
    .filter((p) => p.lang === lang && p.frontmatter.featured === true)
    .sort((a, b) => new Date(b.frontmatter.date) - new Date(a.frontmatter.date))
    .slice(0, limit);
}

const WORDS_PER_MINUTE = 200;

/** Reading time in whole minutes, computed from word count — not editor
 * input, so it can't go stale when an article is edited. */
export function estimateReadingTime(content) {
  const wordCount = content.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(wordCount / WORDS_PER_MINUTE));
}

/** The text a LARP page actually shows to a reader — for `qa`/`terms`
 * pages (FAQ, Glossary) that's the structured frontmatter, not the
 * (empty) markdown body, so estimateReadingTime() needs this instead of
 * `page.content` directly or it always floors to 1 minute regardless of
 * how many questions/terms are actually listed. */
export function getReadableText({ content, frontmatter }) {
  const qaText = frontmatter.qa?.map(({ question, answer }) => `${question} ${answer}`).join(" ") ?? "";
  const termsText = frontmatter.terms?.map(({ term, definition }) => `${term} ${definition}`).join(" ") ?? "";
  return [content, qaText, termsText].filter(Boolean).join(" ");
}

export function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/(^-+|-+$)/g, "");
}

/** `## `-level markdown headings as {text, id} pairs — the id matches what
 * the ReactMarkdown h2 override in LarpSubpage assigns, so sidebar TOC
 * links land on the right heading. Articles too short to have any (most
 * of them, today) simply get an empty list — callers hide the TOC then. */
export function extractHeadings(content) {
  return [...content.matchAll(/^##\s+(.+)$/gm)].map((match) => {
    const text = match[1].trim();
    return { text, id: slugify(text) };
  });
}

/** Shared "publish/update date" formatting for LARP articles, donation
 * projects, and events (each pass their own `date`/`updated` — or, for
 * events, `publishedAt`/`updatedAt`, since `date` already means the
 * event's own occurrence date there) — same day/month/year shape used
 * for the event's occurrence date too. */
export function formatDate(dateStr, lang) {
  return new Date(dateStr).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function excerpt(content, maxLength = 160) {
  const firstLine = content.split("\n").find((line) => line.trim().length > 0);
  if (!firstLine) return "";
  if (firstLine.length <= maxLength) return firstLine;
  const truncated = firstLine.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return `${truncated.slice(0, lastSpace > 0 ? lastSpace : maxLength)}…`;
}

export function getSpendenProjectsForLang(lang) {
  return spendenProjects.filter((p) => p.lang === lang);
}

export function getSpendenProjectBySlug(lang, slug) {
  return spendenProjects.find((p) => p.lang === lang && p.frontmatter.slug === slug);
}
